/**
 * Zacchaeus coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const zacchaeusColoringPagesPack: ToilePack = {
    slug: 'zacchaeus-coloring-pages',
    category: 'faith',
    name: 'Zacchaeus coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tree', label: 'Up the tree', subject: 'short Zacchaeus climbing a sycamore tree to see over the crowd lining the road in Jericho'},
        {key: 'calling', label: 'Come down', subject: 'Jesus stopping beneath the sycamore and looking up, calling Zacchaeus down from the branches'},
        {key: 'giving', label: 'Giving back', subject: 'Zacchaeus at his house happily handing coins back to the people and the poor, Jesus seated at his table'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['jericho street', 'tall tree', 'happy crowd'],
    directionHint: 'try: jericho street',
    seo: {
        title: 'Zacchaeus Coloring Pages: Luke 19 in Three Scenes',
        metaDescription: 'Nine Zacchaeus coloring pages for Sunday school from Luke 19: climbing the sycamore, Jesus calling him down and giving back, in three styles.',
        h1: 'Zacchaeus coloring pages: up the tree, come down, giving back',
        tagline: 'Nine pages of Luke 19 in three scenes, each in three line weights.',
        intro: 'Printable Zacchaeus coloring pages for Sunday school and Christian homeschool, from Luke 19. Three scenes: short Zacchaeus climbing a sycamore to see over the crowd in Jericho, Jesus stopping beneath the tree to call him down, and Zacchaeus handing coins back to the people and the poor. Bold, classic and fine-detail lines. Type a different scene, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Luke 19',
            "a children's church craft table",
            'a Christian homeschool Gospel unit',
            'a bulletin board on generosity',
        ],
        faq: [
            {q: 'Which part of Luke 19 do these show?', a: 'Zacchaeus in the tree, Jesus calling him down, and Zacchaeus giving back.'},
            {q: 'Is the tree a sycamore?', a: 'Yes. The tree is a sycamore, as in Luke 19.'},
            {q: 'Can I ask for the street in Jericho?', a: 'Yes. Type the scene you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
